// Read-only Visualisierung der Workflow-Schritte als senkrechtes Flussdiagramm.
// Jeder Schritt wird zu einer Karte mit Typ-Icon, Name, Zusammenfassung und
// Liste der Eingabe-Referenzen (welche vorigen Schritte werden via {{stepN.output...}}
// herangezogen). Pfeile zwischen den Karten zeigen die Reihenfolge.
//
// Bewusst kein Drag-and-Drop oder Edit hier — die Ansicht ist eine Lesehilfe,
// editiert wird im 'Schritte'-Tab.

const TYPE_META = {
  ai_prompt:         { icon: '🤖', label: 'KI-Prompt',          color: '#7c3aed' },
  http_request:      { icon: '🌐', label: 'HTTP-Request',       color: '#0ea5e9' },
  data_source_query: { icon: '💾', label: 'Datenquelle',        color: '#10b981' },
  transform:         { icon: '🔀', label: 'Transformation',     color: '#f59e0b' },
  condition:         { icon: '⚖️', label: 'Bedingung',          color: '#ef4444' },
  create_task:       { icon: '📋', label: 'Aufgabe anlegen',    color: '#6366f1' },
  email_send:        { icon: '📧', label: 'E-Mail senden',      color: '#ec4899' }
};

// Findet alle {{stepX.output.feld}}-Referenzen im config-Objekt (rekursiv).
// Liefert ein Set der referenzierten Step-IDs.
function findStepRefs(value, acc = new Set()) {
  if (value == null) return acc;
  if (typeof value === 'string') {
    const re = /\{\{\s*(step[\w-]+)\.output/g;
    let m;
    while ((m = re.exec(value)) !== null) acc.add(m[1]);
    return acc;
  }
  if (Array.isArray(value)) {
    value.forEach(v => findStepRefs(v, acc));
    return acc;
  }
  if (typeof value === 'object') {
    Object.values(value).forEach(v => findStepRefs(v, acc));
    return acc;
  }
  return acc;
}

// Kompakte 1-Zeilen-Zusammenfassung der Step-Config — zeigt das wichtigste Feld.
function summarizeConfig(step) {
  const c = step.config || {};
  switch (step.type) {
    case 'ai_prompt':
      return c.prompt ? truncate(c.prompt, 80) : '(kein Prompt)';
    case 'http_request':
      return `${c.method || 'GET'} ${c.url || '(keine URL)'}`;
    case 'data_source_query':
      if (c.query) return `SQL: ${truncate(c.query, 70)}`;
      return `${c.method || 'GET'} ${c.path || '(kein Pfad)'}`;
    case 'transform':
      return typeof c.template === 'object'
        ? `Template: ${truncate(JSON.stringify(c.template), 70)}`
        : truncate(String(c.template || ''), 70);
    case 'condition':
      return c.expression ? `wenn: ${truncate(c.expression, 70)}` : '(keine Bedingung)';
    case 'create_task':
      return c.title ? `Titel: ${truncate(c.title, 70)}` : '(kein Titel)';
    case 'email_send':
      return c.to ? `${truncate(c.to, 30)} — ${truncate(c.subject || '(kein Betreff)', 50)}` : '(kein Empfänger)';
    default:
      return '';
  }
}

function truncate(s, max) {
  if (!s) return '';
  s = String(s).replace(/\s+/g, ' ').trim();
  return s.length > max ? s.slice(0, max - 1) + '…' : s;
}

export default function WorkflowDiagram({ steps = [], runStatus = null }) {
  // runStatus (optional): Map step.id → 'running' | 'completed' | 'failed' | 'pending'.
  // Wird vom Runner befüllt, im Editor null. Färbt die Karten entsprechend.

  if (!steps.length) {
    return (
      <div className="workflow-diagram-empty">
        <p>Noch keine Schritte. Wechsle zum Tab „Schritte", um welche hinzuzufügen oder vom KI-Generator erstellen zu lassen.</p>
      </div>
    );
  }

  return (
    <div className="workflow-diagram">
      {steps.map((step, idx) => {
        const meta = TYPE_META[step.type] || { icon: '❓', label: step.type, color: '#888' };
        const refs = Array.from(findStepRefs(step.config));
        const summary = summarizeConfig(step);
        const status = runStatus?.[step.id];

        return (
          <div key={step.id || idx} className="workflow-diagram-row">
            {idx > 0 && (
              <div className="workflow-diagram-arrow" aria-hidden="true">
                <div className="workflow-diagram-arrow-line" />
                <div className="workflow-diagram-arrow-head">▼</div>
              </div>
            )}
            <div
              className={`workflow-diagram-node ${status ? `status-${status}` : ''}`}
              style={{ borderLeftColor: meta.color }}
            >
              <div className="workflow-diagram-node-head">
                <span className="workflow-diagram-index">{idx + 1}</span>
                <span className="workflow-diagram-icon">{meta.icon}</span>
                <span className="workflow-diagram-name">{step.name || step.id}</span>
                <span className="workflow-diagram-type-tag" style={{ background: meta.color }}>
                  {meta.label}
                </span>
                {status && (
                  <span className={`workflow-diagram-status status-${status}`}>
                    {status === 'running' && '⏳'}
                    {status === 'completed' && '✓'}
                    {status === 'failed' && '✗'}
                    {status === 'skipped' && '↷'}
                    {status === 'pending' && '·'}
                  </span>
                )}
              </div>
              <div className="workflow-diagram-id">id: <code>{step.id}</code></div>
              {summary && (
                <div className="workflow-diagram-summary">{summary}</div>
              )}
              {refs.length > 0 && (
                <div className="workflow-diagram-refs">
                  ← Eingaben: {refs.map(r => (
                    <code key={r} className="workflow-diagram-ref">{r}</code>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
